import { Controller } from "@hotwired/stimulus"
import jsVectorMap from "jsvectormap"

// Connects to data-controller="admin-vector-map"
export default class extends Controller {
  static targets = ["map", "periodSelect"]
  static values = {
    mapType: { type: String, default: "world_merc" },
    zoomButtons: { type: Boolean, default: true },
    regionFill: { type: String, default: "#A9BDFF" },
    regionHoverFill: { type: String, default: "#3056D3" },
    showLabels: { type: Boolean, default: false },
    markers: Array
  }

  async connect() {
    await this.ensureMapDataLoaded()
    this.initMap()
  }
  
  async ensureMapDataLoaded() {
    // Ensure jsVectorMap is globally available for map data files
    if (!window.jsVectorMap) {
      window.jsVectorMap = jsVectorMap
    }
    
    // Check if the map is already registered
    if (!jsVectorMap.maps || !jsVectorMap.maps[this.mapTypeValue]) {
      // Dynamically import the map data
      if (this.mapTypeValue === "world_merc" || this.mapTypeValue === "world") {
        await import("jsvectormap/dist/maps/world-merc.js")
      } else if (this.mapTypeValue === "us_aea_en") {
        await import("../maps/us_aea_en.js")
      }
    }
  }

  disconnect() {
    if (this.map) {
      this.map.destroy()
      this.map = null
    }
  }

  initMap() {
    try {
      // Ensure the map target has an ID for jsVectorMap selector
      if (!this.mapTarget.id) {
        this.mapTarget.id = `jvm-map-${Math.random().toString(36).substr(2, 9)}`
      }
      
      const options = this.buildOptions()
      this.map = new jsVectorMap(options)
    } catch (error) {
      console.error('jsVectorMap initialization error:', error)
    }
  }

  buildOptions() {
    const options = {
      selector: `#${this.mapTarget.id}`,
      map: this.mapTypeValue,
      zoomButtons: this.zoomButtonsValue,

      regionStyle: {
        initial: {
          fontFamily: "Satoshi, sans-serif",
          fill: this.regionFillValue
        },
        hover: {
          fillOpacity: 1,
          fill: this.regionHoverFillValue
        }
      },

      onRegionTooltipShow: (tooltip, code) => {
        this.dispatch("regionTooltip", { detail: { tooltip, code } })
      }
    }

    // Add region labels for US map
    if (this.showLabelsValue && this.mapTypeValue === "us_aea_en") {
      options.regionLabelStyle = {
        initial: {
          fontFamily: "Satoshi, sans-serif",
          fontWeight: "semibold",
          fill: "#fff"
        },
        hover: {
          cursor: "pointer"
        }
      }

      options.labels = {
        regions: {
          render(code) {
            return code.split("-")[1]
          }
        }
      }
    }

    // Add markers if provided
    if (this.hasMarkersValue && this.markersValue.length > 0) {
      options.markers = this.markersValue
      options.markerStyle = {
        initial: {
          fill: "#3056D3",
          stroke: "#fff",
          strokeWidth: 2,
          r: 5
        },
        hover: {
          fill: "#10B981",
          stroke: "#fff",
          strokeWidth: 2,
          r: 7
        }
      }
    }

    return options
  }

  selectPeriod(event) {
    const period = event.target.value
    this.dispatch("periodChange", { detail: { period } })
  }

  // Public method to update region colors
  updateRegionColors(regionColors) {
    if (this.map) {
      Object.keys(regionColors).forEach(code => {
        this.map.setRegionStyle(code, "initial", {
          fill: regionColors[code]
        })
      })
    }
  }

  // Public method to add markers
  addMarkers(markers) {
    if (this.map) {
      this.map.addMarkers(markers)
    }
  }

  // Public method to remove all markers
  removeMarkers() {
    if (this.map) {
      this.map.removeMarkers()
    }
  }

  // Public method to reset map
  reset() {
    if (this.map) {
      this.map.reset()
    }
  }

  // Public method to set focus on a region
  setFocus(params) {
    if (this.map) {
      this.map.setFocus(params)
    }
  }

  // Public method to get selected regions
  getSelectedRegions() {
    if (this.map) {
      return this.map.getSelectedRegions()
    }
    return []
  }

  // Public method to clear selected regions
  clearSelectedRegions() {
    if (this.map) {
      this.map.clearSelectedRegions()
    }
  }
}
